import React, { useState, useEffect } from 'react';
import { Bell, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { api, getNotifications, markNotificationRead, markAllNotificationsRead } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { LeaveDialog } from './LeaveDialog';
import { formatDistanceToNow } from 'date-fns';

export const Header = () => {
  const { user, setUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications();
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleLeaveUpdated = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data && setUser) {
        setUser(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  return (
    <header className="glassmorphic-header sticky top-0 z-40 h-16 flex items-center justify-between px-6 md:px-8" data-testid="header">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 font-outfit">Welcome back, {user?.full_name?.split(' ')[0]}</h2>
        <p className="text-sm text-slate-500">Let's make today productive</p>
      </div>

      <div className="flex items-center space-x-3">
        <Button
          variant="ghost"
          size="icon"
          data-testid="leave-button"
          onClick={() => setShowLeaveDialog(true)}
          className={`w-10 h-10 rounded-xl ${
            user?.status === 'on_leave' ? 'bg-rose-100 text-rose-700 hover:bg-rose-200' : 'hover:bg-slate-100'
          }`}
          title="Manage Leave"
        >
          <CalendarDays className="w-5 h-5" />
        </Button>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-testid="notifications-button"
              className="relative w-10 h-10 rounded-xl hover:bg-slate-100"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0" align="end">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Notifications</h3>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="text-xs text-violet-600 hover:text-violet-700"
                >
                  Mark all read
                </Button>
              )}
            </div>
            <ScrollArea className="h-96">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {notifications.map((notif) => (
                    <div
                      key={notif.notification_id}
                      data-testid={`notification-${notif.notification_id}`}
                      onClick={() => !notif.is_read && handleMarkAsRead(notif.notification_id)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        !notif.is_read ? 'bg-violet-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className="font-semibold text-sm text-slate-900">{notif.title}</p>
                        {!notif.is_read && (
                          <Badge className="bg-violet-500 text-white text-xs">New</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{notif.message}</p>
                      <p className="text-xs text-slate-400">
                        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>

      <LeaveDialog
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
        onLeaveUpdated={handleLeaveUpdated}
      />
    </header>
  );
};